/**
 * Autopilot decision log (bitácora) for a post.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export function BitacoraPanel({ postId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get(`/posts/${postId}/decisions?limit=15`)
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch(() => {
        if (active) setData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [postId]);

  if (loading) return null;
  if (!data || !data.decisions || data.decisions.length === 0) {
    return (
      <div className="mt-3 px-3 py-2 bg-zinc-50 border border-zinc-200 text-[11px] text-muted2">
        🧠 La IA todavía no ha evaluado esta publicación. En la próxima corrida del piloto aparecerá su decisión aquí.
      </div>
    );
  }

  const last = data.decisions[0];
  const meta = ACTION_META[last.action] || ACTION_META.wait;
  const visible = expanded ? data.decisions : data.decisions.slice(0, 3);
  const nextDate = data.next_eval_at ? new Date(data.next_eval_at) : null;
  const nextLabel = nextDate
    ? nextDate.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div
      className="mt-3 border border-zinc-200 bg-white"
      data-testid={`bitacora-${postId}`}
    >
      <header className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted2 font-semibold">
            🧠 Bitácora del bot
          </span>
          <span
            className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5"
            style={{ color: meta.color, backgroundColor: meta.bg }}
            title={last.reason}
          >
            {meta.icon} {meta.label}
          </span>
        </div>
        <div className="text-[10px] text-muted2">
          Próxima revisión: <strong>{nextLabel}</strong>
        </div>
      </header>
      <ol className="divide-y divide-zinc-100">
        {visible.map((d, i) => {
          const m = ACTION_META[d.action] || ACTION_META.wait;
          const dt = d.ts ? new Date(d.ts) : null;
          const when = dt
            ? dt.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
            : "";
          return (
            <li key={`${d.ts || ""}-${d.action}-${i}`} className="px-3 py-2 flex items-start gap-3" data-testid={`decision-${postId}-${i}`}>
              <span
                className="shrink-0 w-7 h-7 inline-flex items-center justify-center text-sm font-bold"
                style={{ color: m.color, backgroundColor: m.bg }}
              >
                {m.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-xs font-semibold" style={{ color: m.color }}>
                    {m.label}
                  </span>
                  <span className="text-[10px] text-muted2 tabular-nums">{when}</span>
                </div>
                <p className="text-xs text-zinc-700 mt-0.5 leading-relaxed">{d.reason}</p>
                {d.signals && (
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted2 tabular-nums">
                    <span>score {d.signals.score}</span>
                    <span>{d.signals.age_hours}h</span>
                    <span>{d.signals.vistas} vistas</span>
                    <span>{d.signals.clicks} clicks</span>
                    {d.signals.confirmed_sales > 0 && (
                      <span className="text-[#1B5E20] font-semibold">
                        {d.signals.confirmed_sales} ventas ✓
                      </span>
                    )}
                  </div>
                )}
                {d.before && d.after && (
                  <div className="mt-2 px-2 py-1.5 bg-[#F3F8FF] border-l-2 border-klein text-[11px]">
                    <div className="text-muted2 text-[10px] mb-0.5">Cambió de:</div>
                    <div className="line-through text-zinc-500 truncate">{d.before.titulo}</div>
                    <div className="text-muted2 text-[10px] mt-1">A:</div>
                    <div className="font-semibold text-ink truncate">{d.after.titulo}</div>
                    <div className="text-[10px] mt-1 text-muted2">
                      Score: {d.before.score} → {d.after.score} · v{d.before.version}→v{d.after.version}
                    </div>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {data.decisions.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-3 py-2 text-[11px] text-klein hover:bg-klein/5 border-t border-zinc-200 font-semibold"
          data-testid={`bitacora-toggle-${postId}`}
        >
          {expanded ? "Mostrar menos" : `Ver ${data.decisions.length - 3} decisiones anteriores`}
        </button>
      )}
    </div>
  );
}


const ACTION_META = {
  wait: { label: "Esperando datos", icon: "⏳", color: "#7C5807", bg: "#FFF8E1" },
  keep: { label: "Va bien · sin cambios", icon: "✅", color: "#1B5E20", bg: "#E8F5E9" },
  full_rewrite: { label: "IA reescribió", icon: "✨", color: "#1E4FCC", bg: "#E7EEFF" },
  boost_visibility: { label: "Falta exposición", icon: "📣", color: "#E65100", bg: "#FFF3E0" },
  improve_visual: { label: "Cambiando portada", icon: "🖼️", color: "#6A1B9A", bg: "#F3E5F5" },
  improve_cta: { label: "Mejorando CTA", icon: "🎯", color: "#AD1457", bg: "#FCE4EC" },
  error: { label: "Error", icon: "⚠️", color: "#B71C1C", bg: "#FFEBEE" },
};
